// src/config/ApiConfig.ts

import axios, { AxiosInstance } from "axios";
import { getClientConfig } from "../../config/clientConfig";
/**
 * ApiConfig class to handle API requests using a singleton pattern.
 * This ensures a single Axios instance is used across the entire application.
 * The base URL and default settings are configured for the API endpoint.
 */
class ApiConfig {
  // Holds the singleton instance of Axios
  private static instance: AxiosInstance;

  // Private constructor to prevent instantiation from other classes
  private constructor() {}

  /**
   * Returns the single instance of Axios. If not created, it initializes the instance
   * with default configurations such as base URL and headers.
   *
   * @returns {AxiosInstance} - Singleton Axios instance
   */
  public static getInstance(): AxiosInstance {
    if (!ApiConfig.instance) {
      const clientConfig = getClientConfig();

      // Create a new Axios instance if it doesn't exist
      ApiConfig.instance = axios.create({
        baseURL: clientConfig.backend_url,
        // process.env.REACT_APP_API_URL,

        headers: {
          "Content-Type": "application/json", // Default content type\
          Authorization: `Bearer ${getTokenFromSomeSource()}`,
        },
      });
    
    ApiConfig.instance.interceptors.request.use(
      (config) => {
        const token = getTokenFromSomeSource();
        const encryptedId = localStorage.getItem("encryptedId");

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
        }

        if (encryptedId) {
          config.headers["X-Application-Type-Id"] = encryptedId;
        } else {
          delete config.headers["X-Application-Type-Id"];
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

      ApiConfig.instance.interceptors.response.use(
        (response) => response,
        (error) => {
          const responseData = error.response?.data;
          const message =
            typeof responseData === "string"
              ? responseData
              : responseData?.message || responseData?.error;

          if (
            typeof message === "string" &&
            message.toLowerCase().includes("user is inactive")
          ) {
            localStorage.removeItem("isAuthenticated");
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.location.replace("/login");
          }

          return Promise.reject(error);
        },
      );
    }

    return ApiConfig.instance;
  }

  public static setApplicationTypeId(encryptedId: string) {
    if (encryptedId) {
      localStorage.setItem("encryptedId", encryptedId);
      ApiConfig.getInstance().defaults.headers.common["X-Application-Type-Id"] =
        encryptedId;
      return;
    }

    localStorage.removeItem("encryptedId");
    delete ApiConfig.getInstance().defaults.headers.common[
      "X-Application-Type-Id"
    ];
  }
}

export default ApiConfig;

const getTokenFromSomeSource = () => {
  const user = localStorage.getItem("user");
  if (user) {
    const userData = JSON.parse(user);
    return userData.jwt;
  }
};
